/**
 * Global Error Handler
 * 
 * Catches all unhandled errors in the application and provides
 * user-friendly error messages while logging for debugging.
 * 
 * @remarks
 * - Implements Angular's ErrorHandler interface
 * - Logs errors appropriately based on environment
 * - Prevents application crashes from unhandled errors
 * - Integrates with error tracking services
 */

import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService } from './logger.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  handleError(error: Error | HttpErrorResponse): void {
    // Run outside Angular zone to prevent infinite loops
    this.zone.runOutsideAngular(() => {
      if (error instanceof HttpErrorResponse) {
        this.handleHttpError(error);
      } else {
        this.handleClientError(error);
      }
    });
  }

  /**
   * Handles HTTP errors
   * @private
   */
  private handleHttpError(error: HttpErrorResponse): void {
    const message = this.getHttpErrorMessage(error);
    this.logger.error(`HTTP Error: ${message}`, error);

    // Navigate to error page for severe errors
    if (error.status === 500 || error.status === 503) {
      this.zone.run(() => {
        // TODO: Create error page component
        // this.router.navigate(['/error'], {
        //   queryParams: { message: 'Service temporarily unavailable' }
        // });
      });
    }
  }

  /**
   * Handles client-side errors
   * @private
   */
  private handleClientError(error: Error): void {
    this.logger.error(`Client Error: ${error.message}`, error);

    // In production, show user-friendly error message
    if (environment.production) {
      // TODO: Show user-friendly notification
      // this.notificationService.showError('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Generates user-friendly HTTP error messages
   * @private
   */
  private getHttpErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      return `Network error: ${error.error.message}`;
    }

    // Server-side error
    switch (error.status) {
      case 0:
        return 'No connection to server. Please check your internet connection.';
      case 400:
        return 'Bad request. Please check your input.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'Access forbidden. You do not have permission.';
      case 404:
        return 'Resource not found.';
      case 500:
        return 'Internal server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable.';
      default:
        return `Server error: ${error.status} ${error.statusText}`;
    }
  }
}






